import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { X, CheckCircle, XCircle } from "lucide-react";
import { ExpiredNotification } from "@/v2/types";
import { formatDate } from "@/v2/utils";
import { useMutation } from "@tanstack/react-query";
import NotificationAction, {
    NotificationActionType,
} from "@/v2/api/notifications/Action";
import { toast } from "sonner";

interface NotificationCardProps {
    employeeDetails: ExpiredNotification;
    category:
        | "expired"
        | "expired_today"
        | "expires_1_day"
        | "expires_2_day"
        | "expires_3_day"
        | "expires_4_day"
        | "expires_5_day"
        | "expires_6_day"
        | "expires_7_day"
        | "expires_8_day"
        | "expires_9_day"
        | "expires_10_day";
    alertExpiryType: "IQAMA_EXPIRY" | "PLANT_ID_EXPIRY";
}

export default function NotificationCard({
    employeeDetails,
    category,
    alertExpiryType,
}: NotificationCardProps) {
    const [isHidden, setIsHidden] = useState(false);

    const Mutation = useMutation({
        mutationFn: (data: NotificationActionType) => NotificationAction(data),
    });

    const handleAction = (action: "approved" | "rejected") => {
        const data: NotificationActionType = {
            employee_id: employeeDetails.id,
            alert_notification_status: action === "approved",
            alert_type: alertExpiryType,
        };

        Mutation.mutate(data, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While approving the mail request, we encountered an issue.`,
                });
            },
            onSuccess: () => {
                toast.success(`Mail sent successfully`);
                setIsHidden(true);
            },
        });
    };

    if (isHidden) return null;

    // Dynamic label and expiry value
    const label = alertExpiryType === "IQAMA_EXPIRY" ? "Iqama" : "Plant ID";
    const expiryDate =
        alertExpiryType === "IQAMA_EXPIRY"
            ? employeeDetails.id_expiry
            : employeeDetails.plant_id_expiry;

    const contentMap: Record<
        NotificationCardProps["category"],
        { title: string; message: string; color: string }
    > = {
        expired: {
            title: `${label} Expired`,
            message: `The ${label} of ${employeeDetails.full_name} has already expired on ${formatDate(
                expiryDate,
                "DD-MMM-YYYY",
            )}.`,
            color: "bg-red-500",
        },
        expired_today: {
            title: `${label} Expiring Today`,
            message: `${employeeDetails.full_name}'s ${label} expires today.`,
            color: "bg-orange-500",
        },
        expires_1_day: {
            title: `${label} Expiring Tomorrow`,
            message: `${employeeDetails.full_name}'s ${label} will expire tomorrow (${formatDate(
                expiryDate,
                "DD-MMM-YYYY",
            )}).`,
            color: "bg-yellow-500",
        },
        expires_2_day: {
            title: `${label} Expiring in 2 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 2 days.`,
            color: "bg-yellow-400",
        },
        expires_3_day: {
            title: `${label} Expiring in 3 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 3 days.`,
            color: "bg-yellow-400",
        },
        expires_4_day: {
            title: `${label} Expiring in 4 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 4 days.`,
            color: "bg-yellow-300",
        },
        expires_5_day: {
            title: `${label} Expiring in 5 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 5 days.`,
            color: "bg-lime-400",
        },
        expires_6_day: {
            title: `${label} Expiring in 6 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 6 days.`,
            color: "bg-lime-400",
        },
        expires_7_day: {
            title: `${label} Expiring in 7 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 7 days.`,
            color: "bg-lime-500",
        },
        expires_8_day: {
            title: `${label} Expiring in 8 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 8 days.`,
            color: "bg-green-500",
        },
        expires_9_day: {
            title: `${label} Expiring in 9 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 9 days.`,
            color: "bg-green-500",
        },
        expires_10_day: {
            title: `${label} Expiring in 10 Days`,
            message: `${employeeDetails.full_name}'s ${label} expires in 10 days.`,
            color: "bg-green-600",
        },
    };

    const content = contentMap[category];

    return (
        <Card className="relative mb-4 border-l-4 border-l-primary shadow-sm transition-shadow hover:shadow-md">
            <button
                onClick={() => setIsHidden(true)}
                className="absolute right-3 top-3 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Dismiss notification"
            >
                <X className="h-5 w-5" />
            </button>

            <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                    <div
                        className={`h-2 w-2 animate-pulse rounded-full ${content.color}`}
                    />
                    <h3 className="text-lg font-semibold text-gray-900">
                        {content.title}
                    </h3>
                </div>
            </CardHeader>

            <CardContent className="space-y-4">
                <div className="text-sm text-gray-700">
                    <p>{content.message} Sent mail to supplier?</p>
                    <p className="mt-2">
                        <span className="font-medium">{label} ID:</span>{" "}
                        {employeeDetails.iqama}
                    </p>
                    <p>
                        <span className="font-medium">Expiry Date:</span>{" "}
                        <span className="font-semibold text-red-600">
                            {formatDate(expiryDate, "DD-MMM-YYYY")}
                        </span>
                    </p>
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        onClick={() => handleAction("approved")}
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-green-700"
                    >
                        <CheckCircle className="h-4 w-4" />
                        Send Mail
                    </button>
                    <button
                        onClick={() => handleAction("rejected")}
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-red-700"
                    >
                        <XCircle className="h-4 w-4" />
                        Reject
                    </button>
                </div>
            </CardContent>
        </Card>
    );
}
